import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type HeroProps = SectionBase & {
  eyebrow: string
  title: string
  subtitle: string
  /** Primary CTA: label; href is a storefront-relative path (e.g. "/store") */
  ctaLabel: string
  ctaHref: string
  ctaSecondaryLabel: string
  ctaSecondaryHref: string
  /** "split" | "centered" | "fullscreen" */
  layout: "split" | "centered" | "fullscreen"
  /** Desktop: first column on split, not used on centered */
  imageSrc: string
  imageAlt: string
}

export const Hero: ComponentConfig<HeroProps> = {
  fields: {
    layout: {
      type: "radio",
      options: [
        { value: "split", label: "Split" },
        { value: "centered", label: "Centered" },
        { value: "fullscreen", label: "Fullscreen" },
      ],
    },
    eyebrow: { type: "text", label: "Eyebrow", contentEditable: true },
    title: { type: "textarea", contentEditable: true },
    subtitle: { type: "textarea", contentEditable: true },
    ctaLabel: { type: "text", label: "Primary CTA", contentEditable: true },
    ctaHref: { type: "text", label: "Primary CTA link" },
    ctaSecondaryLabel: { type: "text", label: "Secondary CTA", contentEditable: true },
    ctaSecondaryHref: { type: "text", label: "Secondary CTA link" },
    imageSrc: { type: "text", label: "Image URL", contentEditable: true },
    imageAlt: { type: "text", label: "Image Alt", contentEditable: true },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    layout: "split",
    eyebrow: "",
    title: "Build something great",
    subtitle: "Start building your landing page with our powerful and flexible components.",
    ctaLabel: "Get started",
    ctaHref: "/store",
    ctaSecondaryLabel: "Learn more",
    ctaSecondaryHref: "",
    imageSrc: "",
    imageAlt: "",
    background: "none",
    paddingY: "lg",
  },

  render: ({
    layout,
    eyebrow,
    title,
    subtitle,
    ctaLabel,
    ctaHref,
    ctaSecondaryLabel,
    ctaSecondaryHref,
    imageSrc,
    imageAlt,
    background,
    paddingY,
  }) => {
    const s = sectionStyle({ background, paddingY })

    if (layout === "split") {
      return (
        <section style={s}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
            <div>
              {eyebrow && <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>{eyebrow}</p>}
              <h1 style={{ fontSize: "clamp(2rem,5vw,3.5rem)", fontWeight: 800, lineHeight: 1.15, marginBottom: 16 }}>{title}</h1>
              <p style={{ fontSize: "clamp(1rem,2vw,1.25rem)", color: "#6b7280", lineHeight: 1.7, marginBottom: 32 }}>{subtitle}</p>
              <div style={{ display: "flex", gap: 12 }}>
                {ctaLabel && (ctaHref
                  ? <a href={ctaHref} style={{ textDecoration: "none" }}><button style={{ padding: "12px 24px", background: "var(--site-accent, #7c3aed)", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaLabel}</button></a>
                  : <button style={{ padding: "12px 24px", background: "var(--site-accent, #7c3aed)", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaLabel}</button>)}
                {ctaSecondaryLabel && (ctaSecondaryHref
                  ? <a href={ctaSecondaryHref} style={{ textDecoration: "none" }}><button style={{ padding: "12px 24px", background: "transparent", color: "inherit", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaSecondaryLabel}</button></a>
                  : <button style={{ padding: "12px 24px", background: "transparent", color: "inherit", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaSecondaryLabel}</button>)}
              </div>
            </div>
            <div>
              {imageSrc
                ? <img src={imageSrc} alt={imageAlt} style={{ width: "100%", borderRadius: 16 }} />
                : <div style={{ width: "100%", aspectRatio: "4/3", background: "#f3f4f6", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 }}>Hero image</div>}
            </div>
          </div>
        </section>
      )
    }

    return (
      <section style={{ ...s, textAlign: "center" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px" }}>
          {eyebrow && <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>{eyebrow}</p>}
          <h1 style={{ fontSize: "clamp(2rem,5vw,3.5rem)", fontWeight: 800, lineHeight: 1.15, marginBottom: 16 }}>{title}</h1>
          <p style={{ fontSize: "clamp(1rem,2vw,1.25rem)", color: "#6b7280", lineHeight: 1.7, marginBottom: 32, maxWidth: 600, marginLeft: "auto", marginRight: "auto" }}>{subtitle}</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {ctaLabel && (ctaHref
              ? <a href={ctaHref} style={{ textDecoration: "none" }}><button style={{ padding: "12px 24px", background: "var(--site-accent, #7c3aed)", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaLabel}</button></a>
              : <button style={{ padding: "12px 24px", background: "var(--site-accent, #7c3aed)", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaLabel}</button>)}
            {ctaSecondaryLabel && (ctaSecondaryHref
              ? <a href={ctaSecondaryHref} style={{ textDecoration: "none" }}><button style={{ padding: "12px 24px", background: "transparent", color: "inherit", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaSecondaryLabel}</button></a>
              : <button style={{ padding: "12px 24px", background: "transparent", color: "inherit", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>{ctaSecondaryLabel}</button>)}
          </div>
        </div>
      </section>
    )
  },
}